import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { COPY } from '../../contracts/copyMap';
import ButtonSpinner from './ButtonSpinner';
import ButtonShimmer from './ButtonShimmer';

// Created once, on first use — NOT at module scope. motion.create() reads the
// component it wraps immediately, and several suites mock react-router-dom
// down to just the hooks they need, which leaves `Link` undefined and throws
// at import time in files that never pass `to` at all. Caching it here keeps
// the component type stable across renders (a fresh type each pass would
// remount the subtree) without paying for it on import.
let motionLink = null;
const getMotionLink = () => {
  if (!motionLink) motionLink = motion.create(Link);
  return motionLink;
};

// Primary CTA. DESIGN.md §6.1 AurumButton (v1.2 — premium pass).
//
// variant="outline"  (default) — 1px Aurum stroke, faint gold gradient fill,
//                    Aurum small-caps type, glow on hover. The sign-in mockup's
//                    "LOG IN TO MOGGED" treatment.
// variant="metallic" — brushed-metal light gradient fill with a slow shimmer
//                    sweep, Obsidian type. The judge feed's "SEND ENCOURAGEMENT".
// variant="plate"    — flat vertical bone plate with a travelling specular
//                    band and an Aurum under-glow, on wider letter-spacing.
//                    The Pro upsell modal's "BEGIN FREE TRIAL". Distinct from
//                    metallic: one moving highlight, not a shimmering fill.
// `loading` is NOT `disabled`. Both stop the click, but disabled means "you
// cannot do this yet" and dims to Aurum Dim; loading means "this is happening"
// and must keep the button's live chrome, or the surface reads as though the
// press failed. So loading keeps the active variant and swaps the label.
const AurumButton = ({
  children,
  onClick,
  // Pass `to` when the button navigates rather than acts — renders a
  // react-router <Link> in identical chrome, keeping middle-click and
  // open-in-new-tab. Ignored while disabled or loading: a link cannot refuse a
  // click, so those states fall back to the <button> that can.
  to,
  disabled = false,
  loading = false,
  loadingLabel = COPY.BUTTON_SUBMITTING,
  type = 'button',
  trailingIcon,
  leadingIcon,
  fullWidth = false,
  size = 'md',
  variant = 'outline',
  className = '',
  ariaLabel,
  'data-testid': testId,
}) => {
  const height = size === 'lg' ? 'h-14' : 'h-12';
  const px = size === 'lg' ? 'px-8' : 'px-7';

  // Dimmed chrome only for a genuine disabled button — a loading one keeps its
  // live treatment so the shimmer reads as progress rather than refusal.
  const variantClasses = disabled && !loading
    ? 'border border-aurum-dim text-bone-dim cursor-not-allowed bg-transparent'
    : variant === 'plate'
      ? [
          'relative overflow-hidden border-0',
          'bg-bone-plate text-obsidian font-semibold',
          'shadow-[0_8px_24px_-6px_rgba(201,164,92,0.5)]',
        ].join(' ')
    : variant === 'metallic'
      ? [
          'relative overflow-hidden border border-transparent',
          'bg-metallic bg-[length:200%_100%] motion-safe:animate-shimmer',
          'text-obsidian font-semibold',
          'shadow-card-lift hover:shadow-aurum-glow-strong',
        ].join(' ')
      : [
          'border border-aurum text-aurum bg-aurum-faint',
          'hover:border-aurum-bright hover:text-aurum-bright hover:shadow-aurum-glow',
          'focus-visible:shadow-aurum-ring',
        ].join(' ');

  const base = [
    'inline-flex items-center justify-center gap-3',
    'font-display uppercase',
    variant === 'plate' ? 'tracking-[0.22em]' : 'tracking-[0.12em]',
    'text-sm',
    height,
    px,
    'rounded-field',
    'transition-all duration-200 ease-mogged',
    'select-none',
    // overflow-hidden matters: ButtonShimmer is absolutely positioned and
    // would otherwise sweep straight past the button's rounded corners.
    // Box-shadows are unaffected — overflow clips descendants, not shadows.
    loading ? 'relative overflow-hidden cursor-wait' : '',
    fullWidth ? 'w-full' : '',
    variantClasses,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconTone =
    variant === 'metallic' || variant === 'plate' ? 'text-obsidian' : 'text-aurum';

  const inner = (
    <>
      {/* The plate's own specular band is dropped while loading as well as
          while disabled — ButtonShimmer is already sweeping, and two
          travelling highlights on one button is noise. */}
      {variant === 'plate' && !disabled && !loading && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 w-[28%] bg-foil-band motion-safe:animate-[foil-sweep_3.4s_ease-in-out_infinite]"
        />
      )}
      {loading && <ButtonShimmer onLight={variant === 'plate' || variant === 'metallic'} />}
      {/* Icons are dropped while loading: the label is replaced, so a trailing
          arrow would point at an action that is already under way — and the
          spinner takes that side. */}
      {leadingIcon && !loading && <span className={`relative z-10 inline-flex items-center ${iconTone}`}>{leadingIcon}</span>}
      <span className="relative z-10">{loading ? loadingLabel : children}</span>
      {trailingIcon && !loading && <span className={`relative z-10 inline-flex items-center ${iconTone}`}>{trailingIcon}</span>}
      {loading && <ButtonSpinner className={`relative z-10 ${iconTone}`} />}
    </>
  );

  const motionProps = {
    whileTap: disabled || loading ? undefined : { scale: 0.985 },
    transition: { duration: 0.15, ease: [0.65, 0, 0.35, 1] },
  };

  if (to && !disabled && !loading) {
    const MotionLink = getMotionLink();
    return (
      <MotionLink
        to={to}
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={testId}
        className={`${base} no-underline`}
        {...motionProps}
      >
        {inner}
      </MotionLink>
    );
  }

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      data-testid={testId}
      className={base}
      {...motionProps}
    >
      {inner}
    </motion.button>
  );
};

export default AurumButton;
